import { API_BASE } from '../config/apiBase';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { 
  Settings,
  Save,
  RefreshCw,
  Target,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE;

const TierConfig = () => {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTierConfig();
  }, []);

  const fetchTierConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/tier-config`);
      setTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Error fetching tier config:', error);
      toast.error('Error al cargar configuración de tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tier-config`, { tiers });
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index, field, value) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="tier-config-loading">
        <Skeleton className="h-12 w-64" />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tier-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">Configuración de Tiers</h1>
            <p className="text-muted-foreground">Define los umbrales para asignación automática de tiers</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-tiers-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Configuración
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 dark:text-blue-400">Cómo funciona la asignación de Tiers</p>
            <p className="text-sm text-blue-700 dark:text-blue-500">
              Los valores de <strong>Min. Depósito</strong> y <strong>Min. Balance Final</strong> son <strong>porcentajes del precio del equipo</strong>.
              Por ejemplo, si el equipo cuesta $100,000 y el tier requiere 15% de depósito mínimo, el solicitante debe tener 
              al menos $15,000 de depósito mínimo en sus estados de cuenta para calificar.
              Solo el Manager puede hacer override manual con justificación.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tier Cards */}
      <div className="space-y-4">
        {tiers.map((tier, index) => (
          <Card key={tier.tier} data-testid={`tier-card-${tier.tier}`}>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg font-['Manrope']">
                    <Input
                      value={tier.name || `Tier ${tier.tier}`}
                      onChange={(e) => updateTier(index, 'name', e.target.value)}
                      className="font-bold border-none p-0 h-auto text-lg bg-transparent"
                      data-testid={`tier-name-${tier.tier}`}
                    />
                  </CardTitle>
                  <CardDescription>
                    <Input
                      value={tier.description || ''}
                      onChange={(e) => updateTier(index, 'description', e.target.value)}
                      placeholder="Descripción del tier..."
                      className="text-sm border-none p-0 h-auto bg-transparent"
                    />
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Min. Depósito (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={tier.min_deposit_pct}
                    onChange={(e) => updateTier(index, 'min_deposit_pct', parseFloat(e.target.value) || 0)}
                    data-testid={`tier-min-deposit-${tier.tier}`}
                  />
                  <p className="text-xs text-muted-foreground">% del precio del equipo</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Min. Balance Final (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={tier.min_ending_balance_pct}
                    onChange={(e) => updateTier(index, 'min_ending_balance_pct', parseFloat(e.target.value) || 0)}
                    data-testid={`tier-min-balance-${tier.tier}`}
                  />
                  <p className="text-xs text-muted-foreground">% del precio del equipo</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">LTV Máximo (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="200"
                    value={tier.max_ltv}
                    onChange={(e) => updateTier(index, 'max_ltv', parseFloat(e.target.value) || 0)}
                    data-testid={`tier-max-ltv-${tier.tier}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Enganche Mínimo (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={tier.min_down_pct}
                    onChange={(e) => updateTier(index, 'min_down_pct', parseFloat(e.target.value) || 0)}
                    data-testid={`tier-min-down-${tier.tier}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TierConfig;
